import { db } from '@/data/firebase/config';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Campaign, CampaignRanking } from '@/domain/types/campaigns';
import { CollectionBase } from '@/domain/types/collections';
import { getDisabledUserIds } from '@/domain/use-cases/get-disabled-user-ids';
import {
  Timestamp,
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { filterAndSortRanking } from '@/domain/use-cases/filter-and-sort-ranking';

export const DASHBOARD_SECTIONS = [
  { collection: DOCUMENTS_DB.RELEASES,          label: 'Última Hora',         path: 'releases' },
  { collection: DOCUMENTS_DB.REPORTS,           label: 'Reportes',            path: 'reports' },
  { collection: DOCUMENTS_DB.NEWSLETTERS,       label: 'Publicaciones',       path: 'newsletters' },
  { collection: DOCUMENTS_DB.VIDEOS,            label: 'Videos',              path: 'videos' },
  { collection: DOCUMENTS_DB.PODCASTS,          label: 'Audios',              path: 'podcasts' },
  { collection: DOCUMENTS_DB.GALLERIES,         label: 'Galería',             path: 'galleries' },
  { collection: DOCUMENTS_DB.CRITICAL_STANDARS, label: 'Estándares Críticos', path: 'critical-standars' },
  { collection: DOCUMENTS_DB.POLICIES,          label: 'Políticas',           path: 'policies' },
] as const;

const AGGREGATE_COLLECTIONS = [
  DOCUMENTS_DB.RELEASES,
  DOCUMENTS_DB.NEWSLETTERS,
  DOCUMENTS_DB.POLICIES,
  DOCUMENTS_DB.REPORTS,
  DOCUMENTS_DB.CRITICAL_STANDARS,
  DOCUMENTS_DB.PODCASTS,
  DOCUMENTS_DB.VIDEOS,
] as const;

const SECTION_PATH: Record<string, string> = {
  [DOCUMENTS_DB.RELEASES]: 'releases',
  [DOCUMENTS_DB.NEWSLETTERS]: 'newsletters',
  [DOCUMENTS_DB.POLICIES]: 'policies',
  [DOCUMENTS_DB.REPORTS]: 'reports',
  [DOCUMENTS_DB.CRITICAL_STANDARS]: 'critical-standars',
  [DOCUMENTS_DB.PODCASTS]: 'podcasts',
  [DOCUMENTS_DB.VIDEOS]: 'videos',
};

export type TopMentionedPublication = {
  id: string;
  title: string;
  mentionCount: number;
  detailPath: string;
};

// Same regex and normalization as the Cloud Function (hashtagMentions.ts)
const HASHTAG_REGEX = /#[\wÀ-ž]+/g;
function extractHashtagsFromText(text: string): string[] {
  const matches = text.match(HASHTAG_REGEX) ?? [];
  return Array.from(new Set(matches.map((h) => h.toLowerCase())));
}

const useDashboard = () => {
  const [sections, setSections] = useState<Record<string, CollectionBase[]>>({});
  const [totalUsersLoggedIn, setTotalUsersLoggedIn] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [totalFavorites, setTotalFavorites] = useState(0);
  const [totalComments, setTotalComments] = useState(0);
  const [totalDownloads, setTotalDownloads] = useState(0);
  const [activeCampaigns, setActiveCampaigns] = useState<Campaign[]>([]);
  const [campaignRankings, setCampaignRankings] = useState<Record<string, CampaignRanking[]>>({});
  const [campaignTopPublications, setCampaignTopPublications] = useState<Record<string, TopMentionedPublication[]>>({});

  const fetchData = useCallback(async () => {
    try {
      // Section cards (top 3 per section) + all docs for totals — in parallel
      const [sectionSnaps, allDocsSnaps] = await Promise.all([
        Promise.all(
          DASHBOARD_SECTIONS.map(({ collection: col }) =>
            getDocs(query(collection(db, col as string), orderBy('createdAt', 'desc'), limit(3))),
          ),
        ),
        Promise.all(
          AGGREGATE_COLLECTIONS.map((col) =>
            getDocs(query(collection(db, col as string), orderBy('createdAt', 'desc'))),
          ),
        ),
      ]);

      // Section data for cards
      const sectionData: Record<string, CollectionBase[]> = {};
      DASHBOARD_SECTIONS.forEach(({ collection: col }, i) => {
        sectionData[col] = sectionSnaps[i].docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as CollectionBase),
        }));
      });
      setSections(sectionData);

      // Totals from all docs
      const allDocs = allDocsSnaps.flatMap((snap) =>
        snap.docs.map((doc) => doc.data() as CollectionBase),
      );
      setTotalViews(allDocs.reduce((acc, d) => acc + (d.views ?? 0), 0));
      setTotalFavorites(allDocs.reduce((acc, d) => acc + (d.favorites ?? 0), 0));
      setTotalComments(allDocs.reduce((acc, d) => acc + (d.comments ?? 0), 0));
      setTotalDownloads(allDocs.reduce((acc, d) => acc + (d.downloads ?? 0), 0));

      // Users logged in
      const usersSnap = await getCountFromServer(
        query(collection(db, 'users'), where('isNew', '==', false)),
      );
      setTotalUsersLoggedIn(usersSnap.data().count);

      // Active campaigns
      const now = Timestamp.now();
      const campaignsSnap = await getDocs(
        query(
          collection(db, DOCUMENTS_DB.CAMPAIGNS),
          where('isActive', '==', true),
          where('startDate', '<=', now),
          orderBy('startDate', 'desc'),
        ),
      );
      const campaignsData = campaignsSnap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<Campaign, 'id'>),
      }));
      setActiveCampaigns(campaignsData);

      if (campaignsData.length > 0) {
        const perCampaignResults = await Promise.all(
          campaignsData.map(async (c) => {
            const hashtagLower = c.hashtag.toLowerCase();

            // Step 1: rankings + publications that have this hashtag in commentHashtags
            const [rankingSnap, ...pubSnaps] = await Promise.all([
              getDocs(
                query(
                  collection(db, 'campaigns', c.id, 'rankings'),
                  orderBy('mentionCount', 'desc'),
                ),
              ),
              ...AGGREGATE_COLLECTIONS.map((col) =>
                getDocs(
                  query(
                    collection(db, col as string),
                    where('commentHashtags', 'array-contains', hashtagLower),
                  ),
                ),
              ),
            ]);

            const matchedPubs = pubSnaps.flatMap((snap, i) =>
              snap.docs.map((doc) => ({
                id: doc.id,
                title: (doc.data() as CollectionBase).title,
                colName: AGGREGATE_COLLECTIONS[i] as string,
              })),
            );

            // Step 2: load comments for each matched publication and count mentions
            // (arrayUnion deduplicates commentHashtags so we must count in the comments)
            const pubMentionCounts = await Promise.all(
              matchedPubs.map(async (pub) => {
                const commentsSnap = await getDocs(
                  collection(db, pub.colName, pub.id, 'comments'),
                );
                const count = commentsSnap.docs.reduce((acc, doc) => {
                  const text = (doc.data().comment ?? '') as string;
                  const tags = extractHashtagsFromText(text);
                  return acc + (tags.includes(hashtagLower) ? 1 : 0);
                }, 0);
                return { ...pub, mentionCount: count };
              }),
            );

            const publications = pubMentionCounts
              .filter((p) => p.mentionCount > 0)
              .sort((a, b) => b.mentionCount - a.mentionCount)
              .slice(0, 3)
              .map(({ id, title, mentionCount, colName }) => ({
                id,
                title,
                mentionCount,
                detailPath: `/dashboard/${SECTION_PATH[colName] ?? colName}/details/${id}`,
              }));

            const allCampaignRankings = rankingSnap.docs.map(
              (d) => d.data() as CampaignRanking,
            );
            const disabledIds = await getDisabledUserIds(
              allCampaignRankings.map((r) => r.userId),
            );

            const sortedRankings = filterAndSortRanking(
              allCampaignRankings,
              disabledIds,
            ).slice(0, 5);

            return {
              campaignId: c.id,
              rankings: sortedRankings,
              publications,
            };
          }),
        );

        const rankingsMap: Record<string, CampaignRanking[]> = {};
        const publicationsMap: Record<string, TopMentionedPublication[]> = {};
        perCampaignResults.forEach(({ campaignId, rankings, publications }) => {
          rankingsMap[campaignId] = rankings;
          publicationsMap[campaignId] = publications;
        });
        setCampaignRankings(rankingsMap);
        setCampaignTopPublications(publicationsMap);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  return {
    sections,
    totalUsersLoggedIn,
    totalViews,
    totalFavorites,
    totalComments,
    totalDownloads,
    activeCampaigns,
    campaignRankings,
    campaignTopPublications,
    fetchData,
  };
};

export default useDashboard;
